import { useMemo } from 'react';
import { getColumnHeader, SearchType } from './config/searchConfig';
import { getResultColumnValue, getResultName } from './helpers/resultValueHelpers';
import { deduplicateSuggestionsData } from './helpers/deduplicateSuggestions';
import { formatSimilarityScore } from './helpers/formatSimilarityScore';
import _ from 'lodash';

interface SuggestionsTableProps {
  suggestionsData: any[];
  selectedItems: any[];
  handleCheckboxSelection: (result: any) => void;
  searchType: string;
  selectedFilter: string;
}

const SuggestionsTable = ({
  suggestionsData,
  selectedItems,
  handleCheckboxSelection,
  searchType,
  selectedFilter,
}: SuggestionsTableProps) => {
  if (!Array.isArray(suggestionsData) || suggestionsData.length === 0) return null;

  const deduplicatedData = useMemo(() => deduplicateSuggestionsData(suggestionsData), [suggestionsData]);

  const selectedIds = useMemo(() => {
    return new Set(selectedItems.map(item => JSON.stringify(item)));
  }, [selectedItems]);

  const showSimilarityColumn =
    selectedFilter === 'name' &&
    (searchType === 'DISEASES' || searchType === 'GENES' || searchType === 'CHEMICALS');

  const renderColumnHeaders = () => {
    const headers = ['#', getColumnHeader(searchType as SearchType, selectedFilter, 'primary')];

    if (searchType === 'DISEASES' && selectedFilter !== 'diseaseUMLSCUI') {
      headers.push(getColumnHeader(searchType as SearchType, selectedFilter, 'secondary'));
      headers.push(getColumnHeader(searchType as SearchType, selectedFilter, 'tertiary'));
    } else if (searchType === 'GENES') {
      headers.push(getColumnHeader(searchType as SearchType, selectedFilter, 'secondary'));
      headers.push(getColumnHeader(searchType as SearchType, selectedFilter, 'tertiary'));
    } else if (searchType === 'VARIANTS' && selectedFilter !== 'strID') {
      headers.push(getColumnHeader(searchType as SearchType, selectedFilter, 'secondary'));
      headers.push(getColumnHeader(searchType as SearchType, selectedFilter, 'tertiary'));
    } else if (searchType === 'CHEMICALS' && selectedFilter !== 'CHEMBL') {
      headers.push(getColumnHeader(searchType as SearchType, selectedFilter, 'secondary'));
      headers.push(getColumnHeader(searchType as SearchType, selectedFilter, 'tertiary'));
    } else {
      headers.push(getColumnHeader(searchType as SearchType, selectedFilter, 'secondary'));
    }

    if (showSimilarityColumn) {
      headers.push('Similarity Score');
    }

    headers.push('Select');

    return { headers, colSpanForErrorRow: headers.length - 2 };
  };

  const { headers: columnHeaders, colSpanForErrorRow } = renderColumnHeaders();

  const renderErrorRow = (item: any, rowNum: number) => (
    <tr key={`error-${item.query}`} className="validation-error">
      <td style={{ color: 'red', fontStyle: 'italic' }}>{rowNum}</td>
      <td style={{ color: 'red', fontStyle: 'italic' }}>{item.query}</td>
      <td style={{ color: 'red', fontStyle: 'italic' }} colSpan={colSpanForErrorRow}>
        {item.validationError}
      </td>
    </tr>
  );

  const renderNotFoundRow = (item: any, rowNum: number) => (
    <tr key={`notfound-${item.query}`} className="validation-error">
      <td style={{ color: 'red', fontStyle: 'italic' }}>{rowNum}</td>
      <td style={{ color: 'red', fontStyle: 'italic' }}>{item.query}</td>
      <td style={{ color: 'red', fontStyle: 'italic' }} colSpan={colSpanForErrorRow}>
        Not available
      </td>
    </tr>
  );

  const renderResultRow = (result: any, query: string, rowNum: number) => {
    const resultString = JSON.stringify(result);
    const isSelected = selectedIds.has(resultString);
    const primaryValue = getResultColumnValue(searchType, selectedFilter, result, query);
    const secondaryValue = getResultName(result, searchType, selectedFilter);

    const cells = [<td key="row-num">{rowNum}</td>, <td key="primary">{primaryValue}</td>];

    // Segunda columna
    if (searchType === 'GENES' && selectedFilter === 'geneID') {
      cells.push(
        <td key="secondary">
          {Array.isArray(result.symbol) ? result.symbol.join(', ') : result.symbol || 'Not available'}
        </td>,
      );
    } else {
      cells.push(<td key="secondary">{secondaryValue}</td>);
    }

    // Tercera columna - solo para casos específicos
    if (searchType === 'DISEASES' && selectedFilter !== 'diseaseUMLSCUI' && selectedFilter !== 'name') {
      cells.push(<td key="tertiary">{result.diseaseUMLSCUI || 'Not available'}</td>);
    } else if (searchType === 'DISEASES' && selectedFilter === 'name') {
      cells.push(
        <td key="tertiary">{_.isNull(result.normalizedName) ? 'Not available' : result.normalizedName}</td>,
      );
    } else if (searchType === 'GENES') {
      if (selectedFilter === 'symbol' || selectedFilter === 'geneID') {
        cells.push(<td key="tertiary">{result.ensembl_ids || 'Not available'}</td>);
      } else if (selectedFilter === 'name') {
        cells.push(<td key="tertiary">{result.normalizedName || 'Not available'}</td>);
      } else {
        cells.push(<td key="tertiary">{result.symbol || 'Not available'}</td>);
      }
    } else if (searchType === 'VARIANTS' && selectedFilter !== 'strID') {
      cells.push(<td key="tertiary">{result.strID || 'Not available'}</td>);
    } else if (searchType === 'CHEMICALS' && selectedFilter !== 'CHEMBL' && selectedFilter !== 'name') {
      cells.push(<td key="tertiary">{result.chemID || 'Not available'}</td>);
    } else if (searchType === 'CHEMICALS' && selectedFilter === 'name') {
      cells.push(<td key="tertiary">{result.normalizedName || 'Not available'}</td>);
    }

    if (showSimilarityColumn) {
      cells.push(<td key="similarity">{formatSimilarityScore(result.similarity)}</td>);
    }

    cells.push(
      <td key="checkbox">
        <input
          type="checkbox"
          name="selectedItem"
          checked={isSelected}
          onChange={() => handleCheckboxSelection(result)}
          className="ml-3"
          data-demo-tour="advanced-search-row-checkbox"
        />
      </td>,
    );

    return <tr key={`result-${rowNum}`}>{cells}</tr>;
  };

  return (
    <div className="suggestions-table" data-demo-tour="advanced-search-matches-table">
      <div className="suggestions-table-scroll-x">
        <table className="table panther-table">
          <thead>
            <tr>
              {columnHeaders.map((header, idx) => (
                <th key={idx}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {deduplicatedData.map(item => {
              if (item.type === 'error') {
                return renderErrorRow(item.data, item.rowNum);
              }
              if (item.type === 'notfound') {
                return renderNotFoundRow(item.data, item.rowNum);
              }
              return renderResultRow(item.data, item.query, item.rowNum);
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SuggestionsTable;
